import { Card, Col, Divider, Form, Input, Row, Space, Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import {
    ButtonPrimary,
    ButtonBack,
    ButtonAdd,
    ButtonSecondary,
    ButtonExcel,
    ButtonPrint
} from 'components/Common/Buttons';
import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks';
import CustomAntdTable from 'components/Common/CustomAntdTable';
import { dismissToast, errorToast, successToast, loadingToastId } from 'utils/Toast';
import Constant from 'utils/Constants';
import { sorterTable } from 'utils/Helper';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import useIsMobileHooks from 'hooks/useIsMobileHooks';
import { PRIVILEGE_ID } from 'utils/AccessPermissionConfig';
import SkeletonPage from 'components/Common/SkeletonPage';
import { ButtonAdvanceFilter } from 'components/Common/ButtonAdvanceFilter';
import Error403 from 'components/Error/Error403';
import BaseAdvanceFilter from 'components/CustomDrawer/BaseAdvanceFilter';
import { useNavigate, useParams } from 'react-router-dom';
import useOrgRepo from 'services/repo/useOrgRepo';
import useReportRepo from 'services/repo/useReportRepo';
import useDownloadReportWbsRepo from 'services/repo/useDownloadReportRepo'

export default function WbsDetailReport() {
    const { wbsId } = useParams();
    const isMobile = useIsMobileHooks();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    useTopbarTitleHooks('Report Detail Work Breakdown Structure');

    const { isLoading: isLoadingOrgList, getOrgList } = useOrgRepo(false);
    const { isLoading: isLoadingReport, getReportDetailWbs } = useReportRepo(false);
    const { isLoading: isLoadingDownloadReportWbs, getDownloadExcelWbsReport } = useDownloadReportWbsRepo(false);
    const isLoadingPrivilege = useRef(true);
    const hasPrivilegePage = useRef(true);
    const [isLoadedPrivilege, setIsLoadedPrivilege] = useState(false);

    const [data, setData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [isOpenAdvanceFilter, setIsOpenAdvanceFilter] = useState(false);
    const [wbsDetailInfo, setWbsDetailInfo] = useState({});

    const [filters, setFilters] = useState({ orgId: null });
    const [orgOptions, setOrgOptions] = useState([]);

    const [tableParams, setTableParams] = useState({
        pageNumber: 1,
        pageSize: 10
    });


    const fetchDataOrg = async () => {
        const payload = {
            pageNumber: 1,
            pageSize: 0,
            orderBy: 'orgId',
            isAscending: true,
            filter: { ...filters }
        };
        const res = await getOrgList(payload);
        if (res?.detail === 'OK' && res?.status === 200) {
            const options = res.data.content.map((item) => ({
                label: item?.orgName,
                value: item?.orgId
            }));
            setOrgOptions(options);

            if (options.length === 1) {
                const onlyOption = options[0];
                form.setFieldsValue({ orgId: onlyOption });
                setFilters((prev) => ({ ...prev, orgId: onlyOption.value }));
            }
        }
    };

    const fetchCheckPrivilege = async () => {
        isLoadingPrivilege.current = false;
        hasPrivilegePage.current = true;
        setIsLoadedPrivilege(true);
        dismissToast(PRIVILEGE_ID.AddEditDivision);
    };

    const fetchData = async () => {
        if (!wbsId) return;

        const payload = {
            wbsId,
            pageNumber: tableParams.pageNumber,
            pageSize: tableParams.pageSize
        };

        const res = await getReportDetailWbs(payload);

        if (res?.detail === 'OK' && res?.status === 200) {
            const responseData = res.data;

            setWbsDetailInfo({
                wbsId: wbsId,
                wbsNo: responseData.wbsNo,
                wbsYear: responseData.wbsYear,
                orgName: responseData.orgName,
                divName: responseData.divName,
                deptName: responseData.deptName,
                posName: responseData.posName,
                posResourceTotal: responseData.posResourceTotal,
                totalFte: responseData.totalFte
            });

            setData(responseData.wbsContentList || []);
            setTotalData(responseData.totalElements || 0); 

        }
    };

    const handleTableChange = (pagination) => {
        setTableParams(prev => ({
            ...prev,
            pageNumber: pagination.current,
            pageSize: pagination.pageSize
        }));
    };

    const getCurrentDateTime = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}${month}${day}${hours}${minutes}${seconds}`;
    };


    const handleExport = async (wbsId, wbsNo) => {
        const key = 'loadingExportWbs';
        loadingToastId('Exporting file...', key);

        try {
            const response = await getDownloadExcelWbsReport({
                wbsId: wbsId
            });

            console.log('Response data = ' + response.data)
            const blob = new Blob(
                [response.data],
                { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
            );
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${getCurrentDateTime()}-${`Report WBS No ${wbsNo}` || 'Report WBS'}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            dismissToast(key);
            successToast('File successfully downloaded.');
        } catch (error) {
            dismissToast(key);
            errorToast('Export failed.');
            console.error("Export failed:", error);
            if (error?.response) {
                console.error('📡 Status:', error.response.status);
                console.error('📄 Data:', error.response.data);
            }
        }
    };


    useEffect(() => {
        fetchCheckPrivilege();
    }, []);

    useEffect(() => {
        if (isLoadedPrivilege) {
            fetchData();
        }
        fetchDataOrg();
    }, [tableParams, isLoadedPrivilege]);


    const columns = [
        {
            title: 'No.',
            dataIndex: 'wbsContentNo',
            align: 'center',
            render: (_, row) => row.wbsContentNo || '-'
        },
        {
            title: 'Uraian Tugas',
            dataIndex: 'wbsContentTask',
            render: (_, row) => row.wbsContentTask || '-'
        },
        {
            title: 'Satuan Hasil',
            dataIndex: 'wbsContentOutputUnitParamValue',
            render: (_, row) => row.wbsContentOutputUnitParamValue || '-'
        },
        {
            title: 'Waktu Penyelesaian',
            dataIndex: 'wbsContentCompletionTime',
            render: (_, row) => row.wbsContentCompletionTime
        },
        {
            title: 'Satuan Waktu',
            dataIndex: 'wbsTuParamValue',
            render: (_, row) => row.wbsTuParamValue
        },
        {
            title: 'Tipe Frekuensi',
            dataIndex: 'wbsContentFrequencyTypeParamValue',
            render: (_, row) => row.wbsContentFrequencyTypeParamValue
        },
        {
            title: 'Hasil Kerja',
            dataIndex: 'wbsContentWorkResult',
            render: (_, row) => row.wbsContentWorkResult
        },
        {
            title: 'FTE',
            dataIndex: 'wbsContentFte',
            render: (_, row) => row.wbsContentFte
        },
        {
            title: 'Remarks',
            dataIndex: 'wbsContentRemark',
            render: (_, row) => row.wbsContentRemark || '-'
        }
    ];

    return (
        <>
            {hasPrivilegePage.current ? (
                <Space direction="vertical" size="large" className="space-vertical-flex">
                    <Card size="big" bordered={false}>
                        {isLoadingPrivilege.current ? (
                            <SkeletonPage />
                        ) : (
                            <>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={20} xl={18}></Col>
                                </Row>
                                <Divider dashed className="mb-1 mt-1" />

                                {isMobile ? (
                                    <ButtonAdvanceFilter onClick={() => setIsOpenAdvanceFilter(true)} />
                                ) : (
                                    <>
                                        {/* {filterUI()} */}
                                        {wbsDetailInfo?.wbsNo && (
                                            <Card
                                                type="inner"
                                                title={
                                                    <div>
                                                        INFORMASI WBS —
                                                        No : <span style={{ fontWeight: 'normal' }}>{wbsDetailInfo?.wbsNo || '-'}</span>
                                                    </div>
                                                }
                                                className="mb-3"
                                            >
                                                <Row gutter={[16, 8]}>
                                                    <Col span={12}>
                                                        {/* <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>No WBS</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.wbsNo || '-'}</span>
                                                        </div> */}
                                                        <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>Organisasi</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.orgName || '-'}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>Divisi</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.divName || '-'}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>Departemen</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.deptName || '-'}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>Posisi</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.posName || '-'}</span>
                                                        </div>

                                                    </Col>
                                                    <Col span={12}>

                                                        <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>Tahun</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.wbsYear || '-'}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>Jumlah SDM</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.posResourceTotal || '0'}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>FTE Total</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.totalFte || '0'}</span>
                                                        </div>
                                                        {/* <div style={{ display: 'flex', lineHeight: '28px', fontSize: '14px' }}>
                                                            <span style={{ width: 130, fontWeight: 600 }}>Departemen</span>:&nbsp;
                                                            <span>{wbsDetailInfo?.deptName || '-'}</span>
                                                        </div> */}
                                                    </Col>
                                                </Row>
                                            </Card>

                                        )}
                                    </>
                                )}

                                <CustomAntdTable
                                    columns={columns}
                                    dataSource={data}
                                    rowKey="wbsContentNo"
                                    tableParam={tableParams}
                                    totalData={totalData}
                                    loading={isLoadingReport}
                                    onChange={handleTableChange}
                                />

                                <Col span={24} offset={10}>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <ButtonExcel buttonText="Download" onClick={() => handleExport(wbsDetailInfo.wbsId, wbsDetailInfo?.wbsNo)} />
                                         <ButtonBack onClick={() => navigate(APP_ROUTE_PREFIX + '/' + RouteConstant.report.reportWbs)} />
                                    </div>
                                </Col>
                            </>
                        )}
                    </Card>
                </Space>
            ) : (
                <Error403 />
            )}

            {isOpenAdvanceFilter && (
                <BaseAdvanceFilter onClose={() => setIsOpenAdvanceFilter(false)} onSearchClick={() => { }}>
                    {/* {filterUI()} */}
                </BaseAdvanceFilter>
            )}
        </>
    );
}
